import * as React from 'react';
import type { IdentityOcrResult } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { ApiHatasi, apiForm } from '@/lib/api';
import { kucultVeJpeg, videodanKare } from '@/lib/gorsel';
import { useKamera } from '@/lib/kamera';
import { BasariRozeti, KimlikRehberi } from './KimlikRehberi';

interface Props {
  reservationId?: string | null;
  onSonuc: (sonuc: IdentityOcrResult, onizleme: string) => void;
  onManuel: () => void;
}

type Durum = 'hazir' | 'okuyor';

/**
 * Hangi yuz okutuluyor.
 *
 * Yeni cipli TC kimlik kartinda ANNE ADI, BABA ADI ve DOGUM YERI arka
 * yuzdedir; anne ve baba adi KBS'de ZORUNLUDUR. Arka yuz onceden istege
 * bagli bir "dosya sec" dugmesiydi ve on yuzden ONCE secilmesi gerekiyordu -
 * pratikte kimse kullanmiyor, gorevli iki alani her seferinde elle yaziyordu.
 * Artik akis iki adimlidir ve arka yuz varsayilan olarak ISTENIR.
 */
type Yuz = 'on' | 'arka';

/**
 * Adim 1 - "Kimligi kameraya goster".
 *
 * Akis: kamera karesi -> istemcide 1600px JPEG -> POST /api/ocr/identity
 * OCR her kosulda ISTEGE BAGLIDIR: hata olursa elle giris acilir.
 *
 * Kamera `useKamera` kancasindadir; `<video>` HER ZAMAN DOM'da durur (bkz.
 * lib/kamera.ts). Onceden yalnizca "kamera" durumunda basiliyordu ve akis
 * bagslanacagi anda ref null olabiliyordu.
 */
export function KimlikYakala({ reservationId, onSonuc, onManuel }: Props) {
  const kamera = useKamera();
  const [durum, setDurum] = React.useState<Durum>('hazir');
  const [hata, setHata] = React.useState<string | null>(null);
  const [yuz, setYuz] = React.useState<Yuz>('on');
  /** Kisa omurlu "alindi" rozeti; ekrani kilitlemez, kart cevrilirken soner. */
  const [basari, setBasari] = React.useState(false);
  /** On yuz cekildi, arka bekleniyor. Ikisi TEK OCR istegiyle gider. */
  const [onYuz, setOnYuz] = React.useState<Blob | null>(null);

  /** Iki yuz TEK istekte gider; model ikisini birlestirip tek kayit doner. */
  async function oku(on: Blob, arka: Blob | null) {
    setDurum('okuyor');
    setHata(null);
    const onizleme = URL.createObjectURL(on);
    try {
      const form = new FormData();
      form.append('gorsel', on, 'kimlik-on.jpg');
      if (arka) form.append('arkaYuz', arka, 'kimlik-arka.jpg');
      if (reservationId) form.append('reservationId', reservationId);

      const sonuc = await apiForm<IdentityOcrResult>('/api/ocr/identity', form);
      kamera.kapat();
      setOnYuz(null);
      setYuz('on');
      onSonuc(sonuc, onizleme);
    } catch (e) {
      setDurum('hazir');
      setHata(e instanceof ApiHatasi ? e.message : 'Kimlik okunamadı. Bilgileri elle girebilirsiniz.');
    }
  }

  /** Cekilen kareyi sirasina gore ya bekletir ya da ikisini birden yollar. */
  async function kareAl(kare: Blob) {
    if (yuz === 'on') {
      setOnYuz(kare);
      setYuz('arka');
      setHata(null);
      /*
       * "Başarılı — şimdi arka yüzü taratın" sahnesi 3 sn surer; kilavuz kart
       * perde sonerken (2,1 sn) cevrilir, yani perde kalktiginda ekran zaten
       * arka yuz adiminda olur. Sahne ekrani kilitlemez: gorevli beklemeden
       * fotograf cekebilir.
       */
      setBasari(true);
      window.setTimeout(() => setBasari(false), 3000);
      return;
    }
    await oku(onYuz!, kare);
  }

  async function kareYakala() {
    if (!kamera.videoRef.current || !kamera.acik) return;
    try {
      await kareAl(await videodanKare(kamera.videoRef.current));
    } catch {
      setHata('Kamera görüntüsü henüz hazır değil. Bir saniye bekleyip tekrar deneyin.');
    }
  }

  async function dosyaSecildi(olay: React.ChangeEvent<HTMLInputElement>) {
    const dosya = olay.target.files?.[0];
    olay.target.value = '';
    if (!dosya) return;
    try {
      await kareAl(await kucultVeJpeg(dosya));
    } catch {
      // Tarayici cozemedigi bir dosyada (bozuk ya da desteklenmeyen bicim)
      // eskiden hicbir sey olmuyordu: ekran duruyor, gorevli neden
      // ilerlemedigini anlamiyordu.
      setHata('Bu dosya okunamadı. Fotoğrafı yeniden çekin ya da JPEG/PNG seçin.');
    }
  }

  /** Pasaportta arka yuz yoktur; gorevli acikca atlayabilsin. */
  async function arkasiYok() {
    if (onYuz) await oku(onYuz, null);
  }

  // Klavye kisayolu: F2 = fotograf cek
  React.useEffect(() => {
    const dinleyici = (e: KeyboardEvent) => {
      if (e.key === 'F2' && kamera.acik) {
        e.preventDefault();
        void kareYakala();
      }
    };
    window.addEventListener('keydown', dinleyici);
    return () => window.removeEventListener('keydown', dinleyici);
  });

  const uyari = hata ?? kamera.hata;

  return (
    <div className="flex flex-col gap-4">
      {/* Iki adimli akis: gorevli hangi yuzde oldugunu her an gormeli */}
      <ol className="flex items-center gap-2 text-[13px]">
        {(['on', 'arka'] as Yuz[]).map((y, i) => {
          const gecti = y === 'on' && yuz === 'arka';
          const simdi = yuz === y;
          return (
            <li key={y} className="flex items-center gap-2">
              <span
                className={
                  'flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ' +
                  (gecti
                    ? 'bg-emerald-500 text-white'
                    : simdi
                      ? 'bg-marka-600 text-white'
                      : 'bg-slate-200 text-slate-500')
                }
              >
                {gecti ? '✓' : i + 1}
              </span>
              <span className={simdi ? 'font-semibold text-slate-900' : 'text-slate-500'}>
                {y === 'on' ? 'Ön yüz' : 'Arka yüz'}
              </span>
              {i === 0 && <span className="h-px w-8 bg-slate-200" />}
            </li>
          );
        })}
        <span className="ml-auto text-[12.5px] text-slate-500">
          İki yüzü de alıyoruz — anne ve baba adı arkada yazıyor
        </span>
      </ol>
      {/*
        Telefonda kimlik DIK tutulur ve 16/10 cerceve cok basik kaliyor:
        belge kucuk gorunuyor, resepsiyonist elini yaklastirmak zorunda.
        Dar ekranda 4/3'e gecip cerceveyi buyutuyoruz.
      */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-slate-900 sm:aspect-[16/10]">
        {/*
          <video> KOSULSUZ basilir. Yalnizca kamera acikken basilsaydi, akis
          baglanacagi anda ref henuz null olur ve goruntu hic gelmezdi.
        */}
        <video
          ref={kamera.videoRef}
          autoPlay
          playsInline
          muted
          className={'size-full object-cover ' + (kamera.acik ? '' : 'hidden')}
        />

        {kamera.acik ? (
          /* Kimlik cercevesi: kullaniciya nereye tutacagini gosterir */
          <>
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-[60%] w-[85%] rounded-xl border-4 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
            {/* Hayalet kart: belgeyi nereye, hangi yonde tutacagi bakar bakmaz anlasilsin */}
            <div className="pointer-events-none absolute inset-0 text-white">
              <KimlikRehberi yuz={yuz} />
            </div>
            <p className="pointer-events-none absolute inset-x-0 top-3 text-center text-[13.5px] font-semibold text-white drop-shadow">
              {yuz === 'on' ? 'Ön yüzü çerçeveye yerleştirin' : 'Kartı çevirin, arka yüzü gösterin'}
            </p>
            {basari && <BasariRozeti />}
          </>
        ) : durum === 'okuyor' ? (
          <div className="flex size-full flex-col items-center justify-center gap-3 text-white">
            <span className="size-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
            <p className="text-lg">Kimlik okunuyor…</p>
            <p className="text-sm text-white/70">Bu işlem birkaç saniye sürer</p>
          </div>
        ) : kamera.aciliyor ? (
          <div className="flex size-full flex-col items-center justify-center gap-3 text-white">
            <span className="size-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
            <p className="text-lg">Kamera açılıyor…</p>
          </div>
        ) : (
          <div className="relative flex size-full flex-col items-center justify-center gap-4 p-5 text-center text-white/80 sm:p-6">
            <div className="text-white">
              <KimlikRehberi yuz={yuz} akista />
            </div>
            <div className="flex flex-col items-center gap-2">
              <p className="text-lg font-semibold text-white sm:text-2xl">
                {yuz === 'on' ? 'Kimliği çerçeveye yerleştirin' : 'Harika — şimdi kartı çevirelim'}
              </p>
              <p className="max-w-md text-sm sm:text-base">
                {yuz === 'on'
                  ? 'Kartı düz bir zemine koymanız yeterli; bilgileri okuyup formu biz dolduruyoruz.'
                  : 'Anne ve baba adı arka yüzde yazıyor. Onu da alalım ki hiçbir alanı elle yazmak zorunda kalmayın.'}
              </p>
            </div>
            {basari && <BasariRozeti />}
          </div>
        )}
      </div>

      {uyari && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">{uyari}</div>
      )}

      {/*
        Dugme duzeni. Telefonda asil eylem TAM GENISLIK alir ve en ustte
        durur; yardimci eylemler altinda ikili izgaraya iner. Masaustunde
        eski tek satirli duzen korunur.
      */}
      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-3">
        {kamera.acik ? (
          <Button boyut="xl" onClick={kareYakala} kisayol="F2" className="w-full sm:w-auto">
            {yuz === 'on' ? 'Ön yüzü çek' : 'Arka yüzü çek ve oku'}
          </Button>
        ) : (
          <Button
            boyut="xl"
            onClick={kamera.ac}
            yukleniyor={kamera.aciliyor || durum === 'okuyor'}
            className="w-full sm:w-auto"
          >
            {yuz === 'on' ? 'Kamerayı aç' : 'Arka yüz için kamerayı aç'}
          </Button>
        )}

        <Button
          boyut="xl"
          varyant="ikincil"
          onClick={() => document.getElementById('dosya-on')?.click()}
          className="w-full whitespace-nowrap px-3 text-base sm:w-auto sm:px-8 sm:text-xl"
        >
          Dosya seç
        </Button>
        <input id="dosya-on" type="file" accept="image/*" capture="environment" hidden onChange={dosyaSecildi} />

        <Button boyut="xl" varyant="sessiz" onClick={onManuel} className="w-full sm:ml-auto sm:w-auto">
          Elle gir
        </Button>
      </div>

      {/*
        Arka yuz adimindaki kacis yollari. Pasaportta arka yuz yoktur, ama
        varsayilan yol iki yuzu de okutmak oldugu icin bunlar ikincil kalir.
      */}
      {yuz === 'arka' && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-[13px]">
          <button
            onClick={() => {
              setOnYuz(null);
              setYuz('on');
            }}
            className="text-slate-500 underline underline-offset-4"
          >
            Ön yüzü tekrar çek
          </button>
          <button onClick={arkasiYok} className="text-slate-500 underline underline-offset-4">
            Arka yüzü yok (pasaport)
          </button>
        </div>
      )}

      <p className="text-[13px] leading-relaxed text-slate-500 sm:text-sm">
        KVKK: Kimlik fotoğrafı yalnızca bilgileri okumak için kullanılır ve tesis ayarında açık değilse
        <strong> saklanmaz</strong>. Bilgiler yasal bildirim (KBS) amacıyla işlenir.
      </p>
    </div>
  );
}
